// magnify.js
import { Component } from '@theme/component';
import { MagnifyMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that provides magnify.js style image zoom functionality.
 *
 * @extends Component
 */
export class Magnify extends Component {
  #overlay = null;
  #currentImage = null;
  #zoomRatio = 2;

  connectedCallback() {
    super.connectedCallback();
    
    // Clean up any existing overlay when component is disconnected
    this.#cleanup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Checks if the screen size is desktop (>= 989px)
   * @returns {boolean}
   */
  #isDesktopScreen() {
    return window.innerWidth >= 989;
  }

  /**
   * Opens the magnify overlay on an image.
   *
   * @param {number} index - The index of the media to magnify.
   * @param {PointerEvent} event - The pointer event.
   * @param {number} zoomRatio - The zoom ratio (default: 2)
   */
  open(index, event, zoomRatio = 1.6) {
    // Check if screen is desktop size
    if (!this.#isDesktopScreen()) {
      // On mobile/tablet, fall back to default behavior or do nothing
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    // Clean up any existing overlay first
    this.#cleanup();
    
    this.#zoomRatio = zoomRatio;
    
    // Get the clicked element and find the image
    const clickedElement = event.target;
    const mediaContainer = clickedElement.closest('.product-media-container');
    
    if (!mediaContainer) {
      // Try to find by index if direct click didn't work
      const mediaGallery = this.closest('media-gallery');
      if (mediaGallery && mediaGallery.refs.media) {
        const mediaContainerByIndex = mediaGallery.refs.media[index];
        if (mediaContainerByIndex) {
          this.#magnifyImageInContainer(mediaContainerByIndex, event);
        }
      }
      return;
    }
    
    this.#magnifyImageInContainer(mediaContainer, event);
  }

  /**
   * Magnifies an image within a media container
   */
  #magnifyImageInContainer(mediaContainer, event) {
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image) return;
    
    this.#currentImage = image;
    this.#createOverlay(image);
    
    // Position based on initial mouse position
    if (event) {
      this.#moveWithHover(event);
    }
    
    // Find index for event dispatch
    const mediaGallery = this.closest('media-gallery');
    if (mediaGallery && mediaGallery.refs.media) {
      const index = mediaGallery.refs.media.indexOf(mediaContainer);
      if (index !== -1) {
        this.dispatchEvent(new MagnifyMediaSelectedEvent(index));
      }
    }
  }

  /**
   * Creates the magnify overlay
   */
  #createOverlay(image) {
    // Create overlay image element
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${image.src}`);
    
    // Create overlay container
    this.#overlay = document.createElement('div');
    this.#prepareOverlay(this.#overlay, overlayImage);
    
    // Show loading state
    image.style.opacity = '50%';
    this.#toggleLoadingSpinner();

    // When overlay image loads
    overlayImage.onload = () => {
      this.#toggleLoadingSpinner();
      image.parentElement.insertBefore(this.#overlay, image);
      image.style.opacity = '100%';
      
      // Add event listeners to overlay
      this.#overlay.addEventListener('click', () => this.close());
      this.#overlay.addEventListener('mousemove', (event) => this.#moveWithHover(event));
      this.#overlay.addEventListener('mouseleave', () => this.close());
    };
  }

  /**
   * Prepares the overlay container
   */
  #prepareOverlay(container, image) {
    container.setAttribute('class', 'image-magnify-full-size');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
    container.style.backgroundColor = 'var(--color-background)';
    container.style.position = 'absolute';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '100%';
    container.style.height = '100%';
    container.style.backgroundRepeat = 'no-repeat';
    container.style.cursor = 'zoom-out';
    container.style.zIndex = '10';
  }

  /**
   * Toggles loading spinner visibility
   */
  #toggleLoadingSpinner() {
    const loadingSpinner = document.querySelector('.magnify-loading__spinner');
    if (loadingSpinner) {
      loadingSpinner.classList.toggle('hidden');
    }
  }

  /**
   * Moves the magnify overlay with mouse movement
   */
  #moveWithHover(event) {
    if (!this.#overlay || !this.#currentImage) return;
    
    const ratio = this.#currentImage.naturalHeight / this.#currentImage.naturalWidth;
    const container = this.#currentImage.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (this.#currentImage.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((this.#currentImage.clientWidth * ratio) / 100)}%`;

    this.#overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    this.#overlay.style.backgroundSize = `${this.#currentImage.width * this.#zoomRatio}px`;
  }

  /**
   * Closes the magnify overlay
   */
  close() {
    this.#cleanup();
  }

  /**
   * Cleans up the magnify overlay
   */
  #cleanup() {
    if (this.#overlay) {
      this.#overlay.remove();
      this.#overlay = null;
    }
    
    if (this.#currentImage) {
      this.#currentImage.style.opacity = '100%';
      this.#currentImage = null;
    }
  }

  /**
   * Sets the zoom ratio
   * @param {number} ratio - The zoom ratio
   */
  setZoomRatio(ratio) {
    this.#zoomRatio = ratio;
  }
}

if (!customElements.get('image-magnify')) {
  customElements.define('image-magnify', Magnify);
}